import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Switch } from "@package/subframe/components/Switch";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateCategoryCommandControllerCreate,
  useUpdateCategoryCommandControllerUpdate,
} from "@/generated/categories/categories";
import type { CategoryResponseDto } from "@/generated/model";

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: CategoryResponseDto | null;
}

/** Invalidate every cached category list (active-only + include-inactive). */
function categoriesPredicate(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === "/v1/categories";
}

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const categorySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(120, "Name must be less than 120 characters"),
  description: z
    .string()
    .trim()
    .max(500, "Description must be less than 500 characters")
    .optional(),
  color: z
    .string()
    .trim()
    .regex(HEX_COLOR_REGEX, "Colour must be a hex code like #4A7C59")
    .optional()
    .or(z.literal("")),
});

export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
}: CategoryFormDialogProps) {
  const isEdit = !!category;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();
  const invalidateCategories = () =>
    queryClient.invalidateQueries({
      predicate: (query) => categoriesPredicate(query.queryKey),
    });

  const createMutation = useCreateCategoryCommandControllerCreate({
    mutation: { onSuccess: invalidateCategories },
  });
  const updateMutation = useUpdateCategoryCommandControllerUpdate({
    mutation: { onSuccess: invalidateCategories },
  });
  const isSaving = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (category) {
      setName(category.name);
      setDescription(category.description ?? "");
      setColor(category.color ?? "");
      setSortOrder(String(category.sortOrder));
      setIsActive(category.isActive);
    } else {
      setName("");
      setDescription("");
      setColor("");
      setSortOrder("");
      setIsActive(true);
    }
    setErrors({});
  }, [category, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const result = categorySchema.safeParse({ name, description, color });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path.join(".")] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});

    const trimmedName = name.trim();
    const trimmedDescription = description.trim() || undefined;
    const trimmedColor = color.trim() || undefined;

    try {
      if (category) {
        await updateMutation.mutateAsync({
          id: category.id,
          data: {
            name: trimmedName,
            sortOrder: Number(sortOrder) || category.sortOrder,
            isActive,
            description: trimmedDescription,
            color: trimmedColor,
          },
        });
        toast.success("Category updated");
      } else {
        await createMutation.mutateAsync({
          data: {
            name: trimmedName,
            sortOrder: sortOrder ? Number(sortOrder) : undefined,
            description: trimmedDescription,
            color: trimmedColor,
          },
        });
        toast.success("Category created");
      }
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(
        `${category ? "Failed to update" : "Failed to create"} category: ${message}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-lg max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">
          {isEdit ? "Edit category" : "Create category"}
        </DialogTitle>
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {isEdit ? "Edit category" : "Create category"}
              </span>
              <span className="text-body font-body text-subtext-color">
                Categories classify compliance tasks across the platform.
              </span>
            </div>
            <IconButton
              type="button"
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* Form content */}
          <div className="flex flex-col gap-4 px-6 py-6">
            <TextField
              className="h-auto w-full"
              size="large"
              label="Name"
              error={!!errors.name}
              helpText={errors.name}
            >
              <TextField.Input
                placeholder="e.g., Health and Safety"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
            </TextField>

            <TextArea
              className="h-auto w-full"
              label="Description (optional)"
              error={!!errors.description}
              helpText={errors.description}
            >
              <TextArea.Input
                placeholder="Describe what this category covers..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
              />
            </TextArea>

            <div className="flex w-full items-center gap-3">
              <TextField
                className="h-auto grow"
                label="Colour (optional)"
                error={!!errors.color}
                helpText={
                  errors.color ??
                  "Enter a hex code, or pick one using the swatch"
                }
              >
                <TextField.Input
                  placeholder="#4A7C59"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  disabled={isSaving}
                />
              </TextField>
              <input
                type="color"
                aria-label="Pick a colour"
                value={
                  HEX_COLOR_REGEX.test(color.trim()) ? color.trim() : "#000000"
                }
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                disabled={isSaving}
                className="h-8 w-8 flex-none cursor-pointer appearance-none rounded-full border border-solid border-secondary-beige-200 bg-transparent p-0 [&::-moz-color-swatch]:rounded-full [&::-moz-color-swatch]:border-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-none"
              />
            </div>

            {isEdit && (
              <>
                <TextField
                  className="h-auto w-full"
                  label="Display order"
                  helpText="Lower numbers appear first in pickers."
                >
                  <TextField.Input
                    type="number"
                    min={1}
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    disabled={isSaving}
                  />
                </TextField>

                <div className="flex items-center gap-3">
                  <Switch
                    checked={isActive}
                    onCheckedChange={setIsActive}
                    disabled={isSaving}
                  />
                  <div className="flex flex-col">
                    <span className="text-body font-body text-default-font">
                      Active
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      Inactive categories are hidden from new tasks but stay on
                      existing ones.
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-secondary"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              loading={isSaving}
              disabled={isSaving}
            >
              {isEdit ? "Save changes" : "Create category"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
